<template>
    <div class="cropper-container">
        <img ref="image" :src="image.url" :alt="image.title" />
    </div>
</template>
<script>
import { Image } from '@/models/Image';
import Cropper from 'cropperjs';
import { delayed } from '@/helpers/tools';

/**
 * Image cropper component - displays an image and manages a
 * CropperJS instance to allow selecting a cropping area.
 *
 * Emits the folloging events:
 *
 * crop({x, y, width, height, ....}) // Info from CropperJS.getData(true)
 */
export default {
    name: 'ImageCropper',
    props: {
        image: {
            type: Image,
            required: true,
        },
        aspectRatio: {
            type: Number,
            default: 0,
        },
        initialData: {
            type: Object,
            default: () => ({}),
        },
        height: {
            type: Number,
            default: 0,
        },
    },

    watch: {
        image(newRec, oldRec) {
            if (newRec !== oldRec) {
                this.initCropper();
            }
        },
    },

    mounted() {
        this.initCropper();
    },

    beforeDestroy() {
        // cleanup cropper instance
        if (this._cropper) {
            this._cropper.destroy();
        }
    },
    methods: {
        // delayed() here gives the DOM a little time to settle: cropper only works if the DOM
        // is done rendering. Also, it de-bouces the init call: it is not executed again within the timeframe.
        initCropper: delayed(function () {
            if (this._cropper) {
                this._cropper.destroy();
            }
            this._cropper = new Cropper(this.$refs.image, {
                viewMode: 2,
                minContainerHeight: this.height || 100,
                initialAspectRatio: this.aspectRatio || NaN,
                aspectRatio: this.aspectRatio || NaN,
                responsive: true,
                scalable: false,
                zoomable: false,
                rotatable: false,
                autoCropArea: 1,
                data: { ...this.initialData },
                crop: () => {
                    this.$emit('crop', this._cropper.getData(true));
                },
            });
        }, 250),
    },
};
</script>

<style lang="scss" scoped>
img {
    display: block;
    max-width: 100%;
    max-height: 100%;
}

.cropper-container {
    overflow: hidden;
    :deep(.cropper-container) {
        overflow: hidden;
    }
    :deep(.cropper-wrap-box) {
        background-color: black;
    }
}
</style>
